/**
 * =============================================================================
 * FRONTEND SERVICE: flightApiService.js  (Duffel — not Amadeus)
 * =============================================================================
 *
 * SUPABASE EDGE FUNCTIONS THIS APP TYPICALLY USES (see supabase/functions/)
 *
 *   flight-search     → searchFlights()     POST …/functions/v1/flight-search
 *   create-booking    → (booking is often done from Payment/Stripe code with
 *                        POST …/functions/v1/create-booking — see StripePaymentForm)
 *
 * THIS FILE
 *   searchFlights()   → Supabase: /flight-search | local Node: /api/flights/search
 *   searchAirports()  → local `airportDatabase` only (no Supabase / Edge calls).
 *   getOfferPricing, createBooking, getBooking, cancelBooking
 *                     → paths under /api/... (Node backend on localhost:3001)
 *
 * REACT_APP_SUPABASE_FUNCTIONS_URL — optional override for …/functions/v1 (default: built-in project URL)
 * REACT_APP_SUPABASE_ANON_KEY — required for browser calls to Edge Functions
 * REACT_APP_API_URL — flight search only; does NOT change airport autocomplete
 * =============================================================================
 */

// Supabase Edge Function base URL (…/functions/v1 — no trailing slash)
const SUPABASE_FUNCTION_URL = 'https://wnsyglbyunngvlcpdhju.supabase.co/functions/v1';
// Supabase anon key (from Settings → API → anon public)
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Induc3lnbGJ5dW5uZ3ZsY3BkaGp1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU4OTUyMDEsImV4cCI6MjA4MTQ3MTIwMX0.qgSiHbYCWqo6qS9ZAsvwxk6aRr0Yq3LQnEp7XnRMHAc';

// Flight / pricing / booking API base (Supabase functions OR local Node)
const API_BASE_URL = process.env.REACT_APP_API_URL || SUPABASE_FUNCTION_URL;

/**
 * Search for flights using Duffel API via Supabase Edge Function
 */
export const searchFlights = async (searchParams) => {
  try {
    // Determine endpoint based on API base URL
    const endpoint = API_BASE_URL.includes('supabase.co') 
      ? `${API_BASE_URL}/flight-search`
      : `${API_BASE_URL}/api/flights/search`;

    console.log('🔍 Flight search using:', endpoint);

    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
    };

    // Always add Supabase anon key when using Supabase (required for authentication)
    if (API_BASE_URL.includes('supabase.co')) {
      if (SUPABASE_ANON_KEY) {
        headers['apikey'] = SUPABASE_ANON_KEY;
        headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
      }
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(searchParams)
    });

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response:', text.substring(0, 200));
      return {
        success: false,
        error: 'Backend server returned an error. Please check your Supabase function is deployed correctly.'
      };
    }

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Flight search failed'
      };
    }

    console.log(`✅ Flight search successful: Received ${data.flights?.length || 0} flights`);
    if (data.flights && data.flights.length > 0) {
      console.log('📊 First flight sample:', {
        id: data.flights[0].id,
        airline: data.flights[0].airline,
        price: data.flights[0].price,
        departure: data.flights[0].departureAirport,
        arrival: data.flights[0].arrivalAirport
      });
    }

    return {
      success: true,
      data: data.flights || []
    };
  } catch (error) {
    console.error('Flight search error:', error);
    
    // Check if it's a JSON parse error
    if (error.message && error.message.includes('JSON')) {
      return {
        success: false,
        error: 'Backend server is not responding correctly. Please check your Supabase function is deployed correctly.'
      };
    }
    
    return {
      success: false,
      error: error.message || 'Failed to connect to server. Please check your Supabase function is deployed correctly.'
    };
  }
};

// Formatting functions are now handled by the backend
// These are kept for any client-side formatting needs

/**
 * Format time from ISO string (client-side helper)
 */
export const formatTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

/**
 * Format duration (e.g., "PT6H10M" to "6h 10m") (client-side helper)
 */
export const formatDuration = (duration) => {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return duration;
  
  const hours = match[1] || '0';
  const minutes = match[2] || '0';
  
  if (hours === '0') return `${minutes}m`;
  if (minutes === '0') return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

/**
 * Search airports — local `airportDatabase` only (no Supabase).
 */
export const searchAirports = async (query) => {
  const q = (query || '').trim();
  if (q.length < 2) {
    return { success: true, data: [], _source: 'local' };
  }

  try {
    const { searchAirportsLocal } = await import('../utils/airportDatabase');
    const results = searchAirportsLocal(q);
    return { success: true, data: results, _source: 'local' };
  } catch (error) {
    console.error('[AirportSearch]', error);
    return { success: false, error: error.message || 'Failed to search airports' };
  }
};

/**
 * Get offer pricing (confirm final price before booking)
 */
export const getOfferPricing = async (offerId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/flights/price`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ offerId })
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to get offer pricing'
      };
    }

    return {
      success: true,
      data: data.offer
    };
  } catch (error) {
    console.error('Get offer pricing error:', error);
    return {
      success: false,
      error: error.message || 'Failed to connect to server'
    };
  }
};

/**
 * Create a flight booking
 */
export const createBooking = async (bookingData) => {
  try {
    const { offerId, passengers, contacts } = bookingData;

    if (!offerId || !passengers || passengers.length === 0) {
      return {
        success: false,
        error: 'Missing required fields: offerId, passengers'
      };
    }

    const response = await fetch(`${API_BASE_URL}/api/bookings/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        offerId,
        passengers,
        contacts
      })
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to create booking'
      };
    }

    return {
      success: true,
      data: data.booking
    };
  } catch (error) {
    console.error('Create booking error:', error);
    return {
      success: false,
      error: error.message || 'Failed to connect to server'
    };
  }
};

/**
 * Get booking details
 */
export const getBooking = async (bookingId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to get booking'
      };
    }

    return {
      success: true,
      data: data.booking
    };
  } catch (error) {
    console.error('Get booking error:', error);
    return {
      success: false,
      error: error.message || 'Failed to connect to server'
    };
  }
};

/**
 * Cancel a booking
 */
export const cancelBooking = async (bookingId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to cancel booking'
      };
    }

    return {
      success: true,
      data: data.cancellation
    };
  } catch (error) {
    console.error('Cancel booking error:', error);
    return {
      success: false,
      error: error.message || 'Failed to connect to server'
    };
  }
};
